import { defineStore } from 'pinia';
import { Node } from './components/storeClasses/node';
import { Topic } from './components/storeClasses/topic';
import { Message } from './components/storeClasses/message';
import { Service } from './components/storeClasses/service';
import { Position } from './components/storeClasses/canvasObjects';
import { V4UUID } from '@/firebase/database';
import { getPackage } from '@/firebase/databaseDependencyInjectables';
import { useGlobalStore } from '../globalModule/store';

export const useNodeCanvasStore = defineStore({
  id: 'nodeCanvas',
  state: (): NodeCanvasState => {
    return {
      workspaceName: 'workspace',
      packageId: undefined,
      workspaceId: undefined,
      favouriteElems: [],
      nodeTemplates: [
        { name: 'None' },
        { name: 'tf_odometry' },
        { name: 'go_to' },
        { name: 'global_costmap' },
        { name: 'amcl' }
      ],
      editingNodeId: 'undefined', // for editing a node
      editingTopicId: 'undefined', // for editing a topic
      editingService: undefined,
      editingMsg: undefined,
      buildCodes: undefined,
      connectingTopicNode: undefined, // set to object containing node and topic ID for connection prompt
      connectingServiceNode: undefined,
      currDragOffset: { x: 0, y: 0 }, // offset for dragging canvas objects
      dragObjId: 'undefined',
      // For saving
      mostRecentSave: undefined, // date time of most recent save
      autosaveTime: 120 // time between autosaves in seconds
    };
  },
  actions: {
    /**
     * Moves any object, node or topic, that matches the given id, to the
     * position {x, y}
     * TODO: uses the global store
     * @param {*} id
     * @param {*} x
     * @param {*} y
     */
    moveObj(id: V4UUID, x: number, y: number) {
      const globalStore = useGlobalStore();
      const elem = globalStore.getCanvasElemById(id);
      if (elem) elem.setPosition(x, y);
    },
    ////////////////////////////////////////////////
    //             External Connections           //
    ////////////////////////////////////////////////
    loadStore(packageId: V4UUID, workspaceId: V4UUID) {
      // Assume we have fetched the db from firebase, if not there will be big issues...
      const packageInfo = getPackage(workspaceId, packageId);
      if (!packageInfo) {
        console.warn('package does not exist');
        return;
      }
      packageInfo.favouriteElems.forEach((ea) =>
        this.favouriteElems.push(Node.fromJSON(ea))
      );
    },
    /**
     * Called upon loading the nodes canvas, decides whether to create a new
     * workspace or load a workspace from localStorage. If creating a new
     * workspace, also stores that workspace in localStorage.workspaces
     */
    initStore(packageName: string, packageId: V4UUID, workspaceID: V4UUID) {
      this.packageId = packageId;
      this.workspaceName = packageName;
      this.workspaceId = workspaceID;
      this.favouriteElems = [];
      this.nodeTemplates = [];
      this.editingNodeId = 'undefined';
      this.editingTopicId = 'undefined';
      this.connectingTopicNode = null;
      this.currDragOffset = { x: 0, y: 0 };
      this.mostRecentSave = undefined;
      this.autosaveTime = 120;

      this.loadStore(packageId, workspaceID);
    }
  }
});

export const primitiveMessages = [
  new Message(
    'String',
    'std_msgs/msg/String',
    [],
    true,
    '73ac9717-b086-4ad7-8b4a-72482b1d6fd9'
  ),
  new Message(
    'MultiArrayLayout',
    'std_msgs/msg/MultiArrayLayout',
    [],
    true,
    '635a2ac9-a015-4a95-abba-c60129da70b3'
  ),
  new Message(
    'UInt16MultiArray',
    'std_msgs/msg/UInt16MultiArray',
    [],
    true,
    '549516d2-0322-45cf-81e5-8ac91f26ffc9'
  ),
  new Message(
    'Int16',
    'std_msgs/msg/Int16',
    [],
    true,
    '118a2a81-6b64-4e9f-b51f-46869d132d68'
  ),
  new Message(
    'UInt32MultiArray',
    'std_msgs/msg/UInt32MultiArray',
    [],
    true,
    'bace689e-a0df-4558-8f02-17898fc9e8f7'
  ),
  new Message(
    'UInt8',
    'std_msgs/msg/UInt8',
    [],
    true,
    'b39a0c04-95ef-4777-93f0-8d7591fcf2a5'
  ),
  new Message(
    'MultiArrayDimension',
    'std_msgs/msg/MultiArrayDimension',
    [],
    true,
    'f4b1cd4c-bfa8-414c-bf69-f10bce92478f'
  ),
  new Message(
    'Empty',
    'std_msgs/msg/Empty',
    [],
    true,
    '7b8c251a-b42f-45b8-b9fe-9a66a9cd9dd1'
  ),
  new Message(
    'UInt8MultiArray',
    'std_msgs/msg/UInt8MultiArray',
    [],
    true,
    '0485472f-bbbe-4b53-a9a0-213ee3da1147'
  ),

  new Message(
    'Int32',
    'std_msgs/msg/Int32',
    [],
    true,
    '0f1410b6-6048-4536-b543-4a879e80764e'
  ),
  new Message(
    'UInt16',
    'std_msgs/msg/UInt16',
    [],
    true,
    '7449e329-80c2-4225-96ae-5e7d9c46d994'
  ),
  new Message(
    'Int32MultiArray',
    'std_msgs/msg/Int32MultiArray',
    [],
    true,
    '2b310def-d767-4c1a-ab7f-069d539f09c9'
  ),
  new Message(
    'UInt64MultiArray',
    'std_msgs/msg/UInt64MultiArray',
    [],
    true,
    '45089907-7aa7-482f-a58d-994fbcc18a70'
  ),
  new Message(
    'Float64',
    'std_msgs/msg/Float64',
    [],
    true,
    '72387648-4abb-49fe-baf1-9c997cd7e162'
  ),
  new Message(
    'Int8',
    'std_msgs/msg/Int8',
    [],
    true,
    '8b59b8e2-b9f2-499f-84e5-6bfe43d1e53a'
  ),
  new Message(
    'Int16MultiArray',
    'std_msgs/msg/Int16MultiArray',
    [],
    true,
    '97df4113-a8a2-4775-bfbb-8d0457f7830e'
  ),
  new Message(
    'ColorRGBA',
    'std_msgs/msg/ColorRGBA',
    [],
    true,
    '32cc3451-05e2-4bb5-a00e-b4cf97dff409'
  ),
  new Message(
    'Float32MultiArray',
    'std_msgs/msg/Float32MultiArray',
    [],
    true,
    'ae18fb79-abd3-4cb4-afef-3ba80b283885'
  ),
  new Message(
    'Int64MultiArray',
    'std_msgs/msg/Int64MultiArray',
    [],
    true,
    '818c10da-108d-4f8e-822f-6202ca4ce89f'
  ),
  new Message(
    'UInt32',
    'std_msgs/msg/UInt32',
    [],
    true,
    'b8b66c72-745a-42ca-8da3-2e8836533724'
  ),
  new Message(
    'Header',
    'std_msgs/msg/Header',
    [],
    true,
    '9411e92d-ff0d-4d17-b7a4-95c612516f36'
  ),
  new Message(
    'Byte',
    'std_msgs/msg/Byte',
    [],
    true,
    'dd15dab8-7805-4de3-8895-0fc419527861'
  ),
  new Message(
    'Float64MultiArray',
    'std_msgs/msg/Float64MultiArray',
    [],
    true,
    'f2444a90-186d-43bb-8b50-b4c378589a6d'
  ),
  new Message(
    'Int64',
    'std_msgs/msg/Int64',
    [],
    true,
    '037177ba-5d8b-4b10-b44c-324aeb3ec2c4'
  ),
  new Message(
    'Float32',
    'std_msgs/msg/Float32',
    [],
    true,
    '91ea076f-e028-43ef-9bd2-5338402794af'
  ),
  new Message(
    'ByteMultiArray',
    'std_msgs/msg/ByteMultiArray',
    [],
    true,
    'be994307-6a17-414f-b6cc-04eba31d925a'
  ),
  new Message(
    'UInt64',
    'std_msgs/msg/UInt64',
    [],
    true,
    '4045e8e9-1854-48f3-a457-17eadab24cf8'
  ),
  new Message(
    'Char',
    'std_msgs/msg/Char',
    [],
    true,
    '61ca49b8-d15a-4879-9ad0-237e62f9d7eb'
  ),
  new Message(
    'Bool',
    'std_msgs/msg/Bool',
    [],
    true,
    '62638f38-3302-40a8-b06b-f39be51ac00c'
  ),
  new Message(
    'Int8MultiArray',
    'std_msgs/msg/Int8MultiArray',
    [],
    true,
    'c41d083d-3b90-45c9-9786-49f8c9e586fb'
  )
];
export class NodeTemplate {
  name: string;
  constructor(name: string) {
    this.name = name;
  }
}

export type NodeCanvasElementsT = Node | Topic | Service;

export type PackageDetail = { id: V4UUID; name: string };

export interface NodeCanvasState {
  workspaceName: string;
  workspaceId?: V4UUID;
  packageId?: V4UUID;
  favouriteElems: Node[];
  nodeTemplates: Array<NodeTemplate>;
  editingNodeId: V4UUID; // for editing a node
  editingTopicId: V4UUID; // for editing a topic
  editingService: Service | undefined;
  editingMsg?: Message;
  connectingTopicNode: { topic: V4UUID; node: V4UUID } | null | undefined; // set to object containing node and topic ID for connection prompt
  connectingServiceNode: { node: Node; service: Service } | null | undefined;
  currDragOffset: Position; // offset for dragging canvas objects
  dragObjId: V4UUID;
  // For saving
  mostRecentSave: Date | undefined; // date time of most recent save
  autosaveTime: number; // time between autosaves in seconds
  buildCodes?: number;
}
